import React, { useState, useEffect } from "react";
import { apifetch } from '../services/interceptors';
import "./JSONFileUploader.css";
import { useTheme } from "../ThemeContext";
import { Alert, Spinner, Form, Button, Card, Container } from "react-bootstrap";

const JSONFileUploader = () => {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isEncrypted, setIsEncrypted] = useState(false);
  const [decryptionKey, setDecryptionKey] = useState("");
  const [exportFormat, setExportFormat] = useState("docx");
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState("");
  const token = localStorage.getItem("token");
  const { isDarkMode } = useTheme();
  const themeClass = isDarkMode ? "bg-dark text-light" : "bg-light text-dark";

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await apifetch("s1/api/organizations", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          setError("Failed to fetch organizations");
          return;
        }
        const data = await response.json();
        const sortedOrgs = (data.organizations || []).sort((a, b) => 
          (a.name || "").localeCompare(b.name || "")
        );
        setOrganizations(sortedOrgs);
        if (sortedOrgs.length > 0) {
          setSelectedOrganization(sortedOrgs[0].id);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setError("");
      setMessage("");
    }, 4000);
    return () => clearTimeout(timer);
  }, [message, error]);

  const handleFileUpload = (event) => {
    const selectedFile = event.target.files[0];

    if (selectedFile && selectedFile.type === "application/json") {
      setFile(selectedFile);
      setError("");

      const reader = new FileReader();
      reader.onload = (e) => {
        const fileContent = e.target.result;

        if (fileContent.startsWith('"')) {
          setIsEncrypted(true);
        } else {
          setIsEncrypted(false);
        }
      };
      reader.readAsText(selectedFile);
    } else {
      setError("Please upload a valid JSON file.");
    }
  };

  const handleParse = async () => {
    if (!file) {
      setError("No file uploaded.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("decryptionKey", decryptionKey);
    formData.append("exportFormat", exportFormat);
    formData.append("organization_id", selectedOrganization);

    setLoading(true);
    setError("");

    try {
      const response = await apifetch("s2/api/uploadfile", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Error while uploading");
      } else {
        setMessage(data.message || "Successfully Uploaded");
      }
    } catch (err) {
      setError("Error uploading the file. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="d-flex justify-content-center align-items-center py-5">
      <Card className={`shadow-lg p-4 w-100 ${themeClass}`} style={{ maxWidth: "600px" }}>
        <Card.Body>
          <h2 className="mb-4 text-center">Upload JSON File</h2>

          {error && <Alert variant="danger">{error}</Alert>}
          {message && <Alert variant="success">{message}</Alert>}

          <Form.Group controlId="fileUpload" className="mb-3">
            <Form.Label>Select a JSON file</Form.Label>
            <Form.Control  className={isDarkMode ? 'bg-dark text-light placeholder-light' : ''} type="file" accept=".json" onChange={handleFileUpload} />
          </Form.Group>

          {isEncrypted && (
            <Form.Group controlId="decryptionKey" className="mb-3">
              <Form.Label>This file is encrypted. Enter decryption key:</Form.Label>
              <Form.Control
                type="password"
                placeholder="Decryption key"
                value={decryptionKey}
                onChange={(e) => {
                  setError("");
                  setDecryptionKey(e.target.value);
                }}
                className={isDarkMode ? 'bg-dark text-light placeholder-light' : ''}
              />
            </Form.Group>
          )}

          <Form.Group controlId="organizationSelect" className="mb-3">
            <Form.Label>Select Organization</Form.Label>
            <Form.Select
              value={selectedOrganization}
              onChange={(e) => setSelectedOrganization(e.target.value)}
              required
              className={isDarkMode ? 'bg-dark text-light placeholder-light' : ''}
            >
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {(file && !isEncrypted) || (isEncrypted && decryptionKey) ? (
            <Button
              className="w-100"
              variant="primary"
              onClick={handleParse}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner className={`${isDarkMode?"text-white placeholder-light":''} me-2`} animation="border" size="sm"/> Uploading...
                </>
              ) : (
                "Upload"
              )}
            </Button>
          ) : null}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default JSONFileUploader;
